import { useQuery } from "@tanstack/react-query";
import type { ViewContext } from "../types";
import { BookingApiError, sendJson } from "../api";
import { BookingLoading, BookingProblem, Notice, RecordNotFound } from "../parts";
import { isOtherSchool } from "../school";
import { useClassroom } from "./manage/useClassroom";
import { parseUnixSeconds, rescheduleOutcome, type RescheduleOutcome } from "./flexitime/flexitime";

// /bookings/<id>/<hash>/<unix>: the customer picked one of the new dates a
// flexitime host offered (EmailFlexitimeRescheduleToCustomer writes one
// link per date). Opening the link is the confirmation, as on v1's
// (school)/(chrome)/bookings/[id]/[hash]/[date] page: POST
// /app/user/bookings/:id/confirm-flexitime-reschedule-request with the
// link's hash (md5 of the buyer's email, which is what lets them in) and
// the date. The segment the router calls `verb` is that hash here.

export function FlexitimeRescheduleView({ ctx, bookingId, verb, date }: { ctx: ViewContext; bookingId: string; verb: string; date: string }) {
  const timestamp = parseUnixSeconds(date);
  const hash = verb.trim();
  // Whose booking it is, before acting on it: another school's booking
  // isn't this site's to reschedule. A failed read doesn't block (the
  // confirmation itself says whether the booking exists).
  const classroom = useClassroom(ctx.api, bookingId);
  const elsewhere =
    classroom.isSuccess &&
    isOtherSchool(ctx.businessId, classroom.data.school, classroom.data.course?.schoolId, classroom.data.booking?.school);
  const checked = classroom.isError || (classroom.isSuccess && !elsewhere);

  // A query rather than a mutation so the confirmation is sent once per
  // link and page, however often React mounts it (the API also ignores a
  // repeat: the same date changes nothing).
  const result = useQuery<RescheduleOutcome>({
    queryKey: ["booking", "flexitime-reschedule", ctx.api, bookingId, hash, timestamp],
    enabled: timestamp !== null && !!hash && checked,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      try {
        await sendJson(`${ctx.api}/app/user/bookings/${encodeURIComponent(bookingId)}/confirm-flexitime-reschedule-request`, "POST", {
          hash,
          timestamp,
        });
        return rescheduleOutcome(null);
      } catch (e) {
        if (e instanceof BookingApiError) return rescheduleOutcome(e.status, e.message);
        throw e;
      }
    },
  });

  if (elsewhere) return <RecordNotFound />;
  const outcome: RescheduleOutcome | undefined = timestamp === null || !hash ? { kind: "bad-link" } : result.data;

  if (!outcome) {
    if (result.isError) {
      return <BookingProblem title="We couldn’t confirm your new date" message="Please try again in a moment." onRetry={() => void result.refetch()} />;
    }
    return <BookingLoading rows={1} />;
  }

  switch (outcome.kind) {
    case "not-found":
      return <BookingProblem title="This page doesn’t exist" message="The booking in this link couldn’t be found." />;
    case "failed":
      return <BookingProblem title="We couldn’t confirm your new date" message="Please try again in a moment." onRetry={() => void result.refetch()} />;
    case "bad-link":
      return (
        <div className="mx-auto max-w-lg py-8">
          <Notice tone="error">
            Your link doesn’t seem correct. Please{" "}
            <a className="font-medium underline underline-offset-4" href={ctx.links.contact()}>
              contact us
            </a>{" "}
            and we will sort it out!
          </Notice>
        </div>
      );
    case "ok":
      return (
        <div className="mx-auto max-w-lg space-y-4 py-8 text-center">
          <h1 className="text-2xl font-semibold">Your booking has been rescheduled</h1>
          <p className="text-muted-foreground">
            Thanks for rescheduling, you will receive a booking confirmation email shortly! In the meantime, you can{" "}
            <a className="font-medium text-primary underline-offset-4 hover:underline" href={ctx.links.home()}>
              browse the rest of the website
            </a>{" "}
            or{" "}
            <a className="font-medium text-primary underline-offset-4 hover:underline" href={ctx.links.manage(bookingId)}>
              view your booking
            </a>
            .
          </p>
        </div>
      );
  }
}
